import { cn } from "@/lib/utils";
import { PopupCard } from "@/Pages/Influencer/PublicProfile/Component";
import { Button } from "@/shadcnComponents/ui/button";
import { Checkbox } from "@/shadcnComponents/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shadcnComponents/ui/tooltip";
import { ArrowRight, InfoIcon, LoaderCircleIcon } from "lucide-react";
import { useState } from "react";
import { BsGraphUpArrow } from "react-icons/bs";
import { IoIosClose } from "react-icons/io";
import { IoEyeOutline, IoPeopleOutline } from "react-icons/io5";
import { useNewCampaign } from "../../CustomHooks/NewCampaignHooks";
import mixpanel from "mixpanel-browser";
import { useGeneralSettings } from "@/Providers/General";

export const PreviewPopUpItem = ({
  icon,
  itemValue,
  itemHead,
  itemClassName,
  infoContent,
}: {
  icon: React.ReactNode;
  itemValue: string | string[];
  itemHead: string;
  bg: string;
  isAnimate: boolean;
  interval?: number;
  prevNumber?: number;
  itemClassName?: string;
  iconClassName?: string;
  infoContent?: React.ReactNode | string;
}) => {
  return (
    <>
      <div className="w-2/5 flex flex-row flex-wrap gap-[15px] font-inter items-center justify-start">
        <div className="w-[24px] h-[24px]  ">{icon}</div>
        <div className="flex flex-wrap flex-col justify-center items-start gap-1">
          <span
            className={cn(
              "text-[color:var(--Natural-800,#424242)] text-sm not-italic font-semibold leading-[normal]",
              itemClassName
            )}
          >
            {Array.isArray(itemValue) ? itemValue.join(",") : itemValue}
          </span>
          <span className=" text-slate-500 text-sm not-italic font-normal leading-[normal">
            {itemHead}
          </span>
        </div>
        <div className="flex">
          {infoContent && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <InfoIcon size={18} className="cursor-pointer" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>{infoContent}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>
    </>
  );
};

export const PreviewPopUp = ({ closable }: { closable?: boolean }) => {
  const { currentStep, currentPreviewList, setOpenReviewPopup, handleNext } =
    useNewCampaign();

  const heading =
    currentStep === 1
      ? "Review Campaign Information"
      : currentStep === 2
      ? "Review Influencer Criteria"
      : "";

  const subHeading =
    currentStep === 1
      ? "Check the details you’ve entered before moving to setting up the influencer selection criteria"
      : currentStep === 2
      ? "Check the details you’ve entered before moving to influencer shortlisting"
      : "";

  const handleClickNext = () => {
    setOpenReviewPopup(false);
    handleNext();
  };

  return (
    <PopupCard
      mainClassName="md:w-[600px]"
      className="md:w-full bg-[color:var(--Shade-White,#FFF)] shadow-[0px_10px_20px_0px_rgba(0,0,0,0.10)] rounded-lg"
      // onClose={onClose}
      children={
        <div className="w-full flex flex-col items-start gap-[33px] flex-wrap">
          <div className="flex w-full flex-col items-start gap-2 shrink-0">
            <span className="flex justify-between items-center self-stretch text-[#121212] text-base not-italic font-semibold leading-6">
              {heading}
              {closable && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="hover:bg-inherit"
                  onClick={() => setOpenReviewPopup(false)}
                >
                  <IoIosClose className="invert" color={"#ffffff"} size={16} />
                </Button>
              )}
            </span>
            <span className="w-[420px] self-stretch text-slate-500 text-sm not-italic font-normal leading-[normal]">
              {subHeading}
            </span>
          </div>
          <div className="w-full flex items-center justify-end ">
            <div className="w-full flex justify-around flex-wrap gap-[33px]">
              {currentPreviewList?.map((item, index) => {
                return (
                  <PreviewPopUpItem
                    key={index}
                    icon={item?.icon}
                    itemValue={
                      item?.value?.length ? item?.value : "Not Selected"
                    }
                    itemHead={item?.title}
                    bg={item?.bg}
                    isAnimate={false}
                    itemClassName="text-md"
                    iconClassName="w-[45px] h-[45px]"
                  />
                );
              })}
            </div>
          </div>
          <div className="w-full flex items-center justify-end ">
            <Button
              className="w-[180px] gap-[10px] "
              onClick={handleClickNext}
              variant="destructive"
            >
              Confirm
              <ArrowRight size={16} />
            </Button>
          </div>
        </div>
      }
    />
  );
};

export const CreatorSelectedPopupCardV2 = ({
  closable,
}: {
  closable?: boolean;
}) => {
  const [currentSection, setCurrentSection] = useState(2);
  const {
    formData,
    currentStep,
    refreshLoading,
    selectedCreators,
    shortlistedInfluencers,
    previewData,
    currentPreviewList,
    handleNext,
    setOpenSelectPopup,
  } = useNewCampaign();

  const heading =
    currentStep === 2
      ? "Review Campaign Outline"
      : "Review Influencer Selection";

  const subHeading =
    currentStep === 2
      ? `Check & confirm the updated numbers for your campaign, on the
      basis of your selection`
      : "Check the details you’ve entered before moving to Collaboration Details";

  const selectedCreatorsLength =
    currentStep === 3
      ? selectedCreators?.length
      : shortlistedInfluencers?.length;

  const EstimatedImpressions =
    currentStep === 3
      ? parseInt(previewData?.ExpectedFinalMinImpression?.toString())
      : parseInt(previewData?.minImpressions?.toString());

  const EstimatedExpense =
    currentStep === 3
      ? parseInt(previewData?.ExpectedSpendAmount?.toString())
      : parseInt(previewData?.ExpectedAllImpressionAmount?.toString());

  const handleClickNext = () => {
    mixpanel.track(
      "Brand Draft Campaign - Select Creator Section: I don't want to add more - clicked"
    );
    handleNext();
    setOpenSelectPopup(false);
  };

  return (
    <PopupCard
      // onClose={onClose}
      mainClassName="md:w-[600px]"
      className="md:w-full bg-[color:var(--Shade-White,#FFF)] shadow-[0px_10px_20px_0px_rgba(0,0,0,0.10)] rounded-lg"
      children={
        currentSection === 1 || currentStep !== 2 ? (
          <div className="w-full flex flex-col gap-[33px]">
            <div className="w-full flex flex-col gap-2">
              <span className="w-full flex justify-between items-center  text-[#121212] text-base not-italic font-semibold leading-6">
                {heading}
                {closable && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="hover:bg-inherit"
                    onClick={() => setOpenSelectPopup(false)}
                  >
                    <IoIosClose
                      className="invert"
                      color={"#ffffff"}
                      size={16}
                    />
                  </Button>
                )}
              </span>
              <span className="w-full text-slate-500 text-sm not-italic font-normal leading-[normal]">
                {subHeading}
              </span>
            </div>

            {/* Metrics with Icons */}
            <div className="w-full flex justify-around ">
              {[
                {
                  icon: <BsGraphUpArrow className="text-red-500 w-4 h-4" />,
                  itemValue: `₹${EstimatedExpense?.toLocaleString()}`,
                  itemHead:
                    formData?.campaignInfo?.collabType === "fixed"
                      ? "Total Expense"
                      : "Expected Expense",
                },
                {
                  icon: <IoEyeOutline className="text-red-500 w-4 h-4" />,
                  itemValue: EstimatedImpressions?.toLocaleString(),
                  itemHead: "Estimated Reach",
                },
                {
                  icon: <IoPeopleOutline className="text-red-500 w-4 h-4" />,
                  itemValue: selectedCreatorsLength,
                  itemHead:
                    currentStep === 2
                      ? "No. of Influencers"
                      : "Selected Influencers",
                },
              ]?.map((item, index) => {
                return (
                  <div
                    key={index}
                    className="flex gap-3 font-inter items-center justify-center"
                  >
                    {item?.icon}

                    {/* Metrics */}
                    <div className="flex flex-wrap flex-col justify-center items-start gap-1">
                      <span className="text-[color:var(--Natural-800,#424242)] text-sm not-italic font-semibold leading-[normal]">
                        {refreshLoading ? (
                          <LoaderCircleIcon className="h-4 w-4 animate-spin " />
                        ) : (
                          item?.itemValue
                        )}
                      </span>
                      <span className="text-slate-500 text-sm not-italic font-normal leading-[normal]">
                        {item?.itemHead}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 justify-end">
              <Button
                disabled={
                  currentStep === 2
                    ? refreshLoading
                    : selectedCreators.length === 0 || refreshLoading
                }
                onClick={() => handleClickNext()}
                variant="destructive"
                className="gap-[10px]"
              >
                Confirm
                <ArrowRight size={16} />
              </Button>
            </div>
          </div>
        ) : (
          <div className="w-full flex flex-col items-start gap-[33px] flex-wrap">
            <div className="flex w-full flex-col items-start gap-2 shrink-0">
              <span className="flex justify-between items-center self-stretch text-[#121212] text-base not-italic font-semibold leading-6">
                Review Influencer Criteria
                {closable && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="hover:bg-inherit"
                    onClick={() => setOpenSelectPopup(false)}
                  >
                    <IoIosClose
                      className="invert"
                      color={"#ffffff"}
                      size={16}
                    />
                  </Button>
                )}
              </span>
              <span className="w-[420px] self-stretch text-slate-500 text-sm not-italic font-normal leading-[normal]">
                Check the details you’ve entered before moving to influencer
                shortlisting
              </span>
            </div>
            <div className="w-full flex items-center justify-end ">
              <div className="w-full flex flex-wrap justify-around  gap-[33px]">
                {currentPreviewList?.map((item, index) => {
                  return (
                    <PreviewPopUpItem
                      key={index}
                      icon={item?.icon}
                      itemValue={
                        item?.value?.length ? item?.value : "Not Selected"
                      }
                      itemHead={item?.title}
                      bg={item?.bg}
                      isAnimate={false}
                      itemClassName="text-md"
                      iconClassName="w-[45px] h-[45px]"
                    />
                  );
                })}
              </div>
            </div>
            <div className="w-full flex items-center justify-end ">
              <Button
                className="w-[180px] gap-[10px] "
                onClick={() =>
                  currentStep === 2 ? setCurrentSection(1) : handleClickNext()
                }
                variant="destructive"
              >
                Confirm
                <ArrowRight size={16} />
              </Button>
            </div>
          </div>
        )
      }
    />
  );
};

export const CheckOutPopupCardV2 = () => {
  const generalState = useGeneralSettings();
  const {
    formData,
    setFormData,
    handleNext,
    setOpenCheckOutPopup,
    setIsReviewDone,
    selectedCreators,
  } = useNewCampaign();

  const PerformancePoints = [
    "Expenditure is based on the reach your campaign generates, aligning costs with actual performance.",
    <div>
      Any funds not used are{" "}
      <b>refunded to you as credits and can be used for future campaigns </b>
      ensuring you only pay for the results
    </div>,
    "Right after your post goes live, budget utilization can increase sharply for 2-5 hours as Reach spikes, after which it tends to stabilize.",
    "Post-virality is considered a bonus and does not increase costs.",
    <div>
      The only control for stopping an influencer marketing campaign midway is
      post deletion, because we lack direct or indirect controls like archiving
      or restricting post visibility. The funds in this case are managed as per
      our{" "}
      <a className="underline" href={`https://www.anchors.in/termsConditions/`}>
        T&C
      </a>
    </div>,
  ];

  const FixedPoints = [
    "Expenditure is fixed, according to the custom commercials you have set for the selected influencers.",
    "Budget utilization does not vary on the basis of the performance of the campaign. Instead, it reflects the amount fixed for each influencer, as they start going live.",
    <div>
      The only control for stopping an influencer marketing campaign midway is
      post deletion, because we lack direct or indirect controls like archiving
      or restricting post visibility. The funds in this case are managed as per
      our{" "}
      <a className="underline" href={`https://www.anchors.in/termsConditions/`}>
        T&C
      </a>
    </div>,
  ];

  const handleCheckChange = () => {
    setFormData((prev) => {
      return {
        ...prev,
        campaignInfo: {
          ...prev.campaignInfo,
          TermsAndConditionAccepted:
            !prev.campaignInfo.TermsAndConditionAccepted,
        },
      };
    });
  };

  const handelClickNext = () => {
    if (!formData?.campaignInfo?.TermsAndConditionAccepted) {
      return generalState?.showErrorToast("Please Accept Terms and Conditions");
    }
    mixpanel.track(
      "Brand Draft Campaign - Campaign Review: Continue to create - clicked"
    );
    setOpenCheckOutPopup(false);
    handleNext();
    setIsReviewDone(true);
  };

  return (
    <PopupCard
      onClose={() => setOpenCheckOutPopup(false)}
      mainClassName="md:w-5/12"
      className="md:w-full bg-[color:var(--Shade-White,#FFF)] shadow-[0px_10px_20px_0px_rgba(0,0,0,0.10)] rounded-lg"
      children={
        <div className="w-full flex flex-col gap-[33px]">
          <div className="w-full flex flex-col gap-2">
            <span className="w-full text-[#121212] text-start text-base not-italic font-semibold leading-[18px]">
              IMPORTANT
            </span>
            <span className="w-full text-[#9093AA] text-start text-sm not-italic font-normal leading-[18px]">
              <ul className="list-disc flex flex-col gap-3 ml-6">
                {(formData?.campaignInfo?.collabType === "fixed"
                  ? FixedPoints
                  : PerformancePoints
                )?.map((item, index) => {
                  return <li key={index}>{item}</li>;
                })}
              </ul>
            </span>
          </div>
          <div className="flex items-center space-x-2 ml-6">
            <Checkbox
              checked={formData?.campaignInfo?.TermsAndConditionAccepted}
              onCheckedChange={handleCheckChange}
              id="terms"
            />
            <label
              htmlFor="terms"
              className="text-black text-sm not-italic font-medium leading-[14px]"
            >
              I have read and understand how funds are managed for my campaign
            </label>
          </div>
          {/* Action Buttons */}
          <div className="flex gap-4 justify-end">
            <Button
              disabled={selectedCreators.length === 0}
              onClick={handelClickNext}
              variant="destructive"
            >
              Continue
            </Button>
          </div>
        </div>
      }
    />
  );
};
